/**
 * A standard drop-down: the browser's own <select>, dressed to match.
 *
 * On a phone it opens the system's picker, the one list every gardener
 * already knows how to use; on a computer it takes the keyboard, typing
 * "tom" jumping to Tomato. Nothing to learn, which is the point.
 *
 * Used by: the plant fields, the plant form, the stage line, the seed-packet
 * review and the photo tray.
 *
 * @module NativeSelect
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/** What NativeSelect takes beyond a <select>'s own attributes. */
interface NativeSelectExtras {
  /** Draws a red border. */
  invalid?: boolean;
  /** Classes for the <select> itself, winning over its own (a smaller text or a narrower arrow gap); `className` goes on the wrapper. */
  selectClassName?: string;
}

/** The browser's <select>, with a chevron and an `invalid` state that draws a red border. */
export const NativeSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement> & NativeSelectExtras>(
  ({ className, invalid, selectClassName, children, ...props }, ref) => (
    // Spacing given by the caller goes on the wrapper, so the arrow stays centred on the box.
    <div className={cn('relative', className)}>
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          'block h-12 w-full appearance-none truncate rounded-md border bg-background pl-3 pr-9 text-base text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
          invalid && 'border-2 border-destructive',
          selectClassName,
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
    </div>
  ),
);
NativeSelect.displayName = 'NativeSelect';
